'use client';

import React, { useEffect, useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';

interface GeneratingScreenProps {
  occasion?: string;
}

const LOADING_PHRASES = [
  'Analysing the occasion aesthetic…',
  'Selecting hero pieces for your look…',
  'Balancing proportions and silhouettes…',
  'Curating colour palette…',
  'Sourcing the perfect accessories…',
  'Finalising your editorial…',
];

const SKELETON_LABELS = ['Top', 'Bottom', 'Shoes', 'Accessory', 'Outerwear', 'Bag'];

const SKELETON_HEIGHTS = ['h-52 sm:h-64', 'h-44 sm:h-56', 'h-48 sm:h-60', 'h-40 sm:h-52', 'h-56 sm:h-68', 'h-44 sm:h-56'];

export default function GeneratingScreen({ occasion }: GeneratingScreenProps) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [visibleCards, setVisibleCards] = useState(0);

  useEffect(() => {
    const phraseTimer = setInterval(() => {
      setPhraseIndex(prev => (prev + 1) % LOADING_PHRASES.length);
    }, 2200);

    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 92) return prev;
        return prev + Math.random() * 4;
      });
    }, 300);

    // Stagger skeleton cards appearing
    const cardTimers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < 6; i++) {
      cardTimers.push(
        setTimeout(() => {
          setVisibleCards(prev => Math.min(prev + 1, 6));
        }, i * 180)
      );
    }

    return () => {
      clearInterval(phraseTimer);
      clearInterval(progressTimer);
      cardTimers.forEach(t => clearTimeout(t));
    };
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-16"
      style={{ backgroundColor: '#FAF7F2' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 sm:gap-3 mb-8 sm:mb-10">
        <AppLogo size={26} />
        <span className="font-serif-display text-xl sm:text-2xl" style={{ color: '#1A1A1A' }}>Atelier.ai</span>
      </div>

      {/* Central status block */}
      <div className="flex flex-col items-center mb-10 sm:mb-14">
        {/* Pulsing orb */}
        <div className="relative mb-6 sm:mb-8">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center animate-pulse-gold"
            style={{
              backgroundColor: 'rgba(201,169,110,0.10)',
              border: '1.5px solid rgba(201,169,110,0.35)',
            }}
          >
            <span className="text-2xl sm:text-3xl select-none">✦</span>
          </div>
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{ animation: 'spin 3s linear infinite', border: '1px dashed rgba(201,169,110,0.28)' }}
          />
        </div>

        {/* Heading */}
        <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-center mb-1.5" style={{ color: '#1A1A1A' }}>
          Styling your look
        </h2>
        {occasion && (
          <p className="text-xs sm:text-sm text-center mb-4" style={{ color: 'hsl(30 8% 52%)' }}>
            for{' '}
            <span className="font-medium italic" style={{ color: '#C9A96E' }}>
              {occasion}
            </span>
          </p>
        )}

        {/* Rotating phrase */}
        <p
          key={`phrase-${phraseIndex}`}
          className="text-xs sm:text-sm text-center mb-6 animate-fade-in"
          style={{ color: 'hsl(30 8% 52%)', minHeight: '20px', letterSpacing: '0.01em' }}
        >
          {LOADING_PHRASES[phraseIndex]}
        </p>

        {/* Progress bar */}
        <div className="w-48 sm:w-64 h-[2px] rounded-full overflow-hidden mb-2" style={{ backgroundColor: 'hsl(35 15% 90%)' }}>
          <div
            className="h-full rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%`, backgroundColor: '#C9A96E' }}
          />
        </div>
        <p className="text-[10px] sm:text-xs tabular-nums" style={{ color: 'hsl(30 8% 62%)' }}>
          {Math.round(progress)}%
        </p>
      </div>

      {/* ── Editorial skeleton preview ── */}
      <div className="w-full max-w-5xl">
        {/* Section label skeleton */}
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <div className="animate-shimmer h-[1px] flex-1 rounded-full" style={{ opacity: 0.5 }} />
          <div className="animate-shimmer h-3 w-28 sm:w-36 rounded-full" style={{ opacity: 0.6 }} />
          <div className="animate-shimmer h-[1px] flex-1 rounded-full" style={{ opacity: 0.5 }} />
        </div>

        {/* Outfit cards grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 mb-6 sm:mb-8">
          {SKELETON_LABELS.map((label, i) => (
            <div
              key={`skeleton-${label}`}
              className="rounded-2xl overflow-hidden"
              style={{
                opacity: visibleCards > i ? 1 : 0,
                transform: visibleCards > i ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.35s ease, transform 0.35s ease',
                backgroundColor: '#fff',
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              }}
            >
              {/* Image placeholder */}
              <div className={`animate-shimmer ${SKELETON_HEIGHTS[i]} w-full`} />

              {/* Card body */}
              <div className="p-3 sm:p-4">
                {/* Category label — editorial uppercase */}
                <div className="flex items-center gap-1.5 mb-2 sm:mb-2.5">
                  <div
                    className="animate-shimmer h-[1px] w-4 rounded-full"
                    style={{ opacity: 0.7 }}
                  />
                  <div
                    className="animate-shimmer h-2 w-10 sm:w-12 rounded-full"
                    style={{ opacity: 0.7 }}
                  />
                </div>

                {/* Item name — serif display style */}
                <div className="animate-shimmer h-4 sm:h-5 rounded-md mb-1.5 sm:mb-2 w-5/6" />
                <div className="animate-shimmer h-3 sm:h-3.5 rounded-md mb-3 sm:mb-4 w-3/5" />

                {/* Price + brand row */}
                <div className="flex items-center justify-between">
                  <div className="animate-shimmer h-3 w-14 sm:w-16 rounded-full" />
                  <div className="animate-shimmer h-3 w-10 sm:w-12 rounded-full" />
                </div>

                {/* CTA button skeleton */}
                <div className="animate-shimmer h-8 sm:h-9 rounded-xl mt-3 sm:mt-4 w-full" style={{ opacity: 0.5 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Budget summary skeleton */}
        <div
          className="rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          style={{ backgroundColor: 'rgba(201,169,110,0.06)', border: '1px solid rgba(201,169,110,0.15)' }}
        >
          <div className="flex flex-col gap-2">
            <div className="animate-shimmer h-2.5 w-20 rounded-full" style={{ opacity: 0.6 }} />
            <div className="animate-shimmer h-5 w-28 sm:w-32 rounded-md" style={{ opacity: 0.7 }} />
          </div>
          <div className="flex gap-2">
            <div className="animate-shimmer h-8 w-24 sm:w-28 rounded-xl" style={{ opacity: 0.5 }} />
            <div className="animate-shimmer h-8 w-20 sm:w-24 rounded-xl" style={{ opacity: 0.4 }} />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}